// https://tiptap.dev/examples/menus

// Resizable area / https://codepen.io/rod911/details/wvzPjEV
// Drag & drop image / https://www.codemzy.com/blog/tiptap-drag-drop-image

import tw from 'twin.macro';
import styled from 'styled-components';
import { useState, useCallback, useRef, useEffect } from 'react';
import { EditorContent } from '@tiptap/react';
import { useDropzone } from 'react-dropzone';
import get from 'lodash.get';
import { useInput } from '@components/inputRichText/useInput';
import { findChildren } from '@tiptap/core';
import axios from 'axios';
import { BubbleToolbar } from '@components/inputRichText/bubbleToolbar';
import { StyledProseMirrorWrapper } from '@components/inputRichText/styles';
import { useRichText } from '@components/inputRichText/useRichText';
import { Templates } from '@components/inputRichText/templates';
import { cleanHtml, formatBytes, scrollToBottom, compressHTML } from '@components/utils';
import { materialInputStyles } from '@components/inputRichText/inputWrapper';
import { CaretdownIcon, Popover, Tooltip } from '@components/componentUtils';
import * as Heroicon from '@heroicons/react/24/outline';
import moment from 'moment';

const ToolbarBtn = styled.button(({ border = true }) => [
  tw`inline-flex items-center bg-white text-xs font-hmedium truncate`,
  border && tw`py-1 px-3 shadow border border-solid border-slate-200 rounded-full`
]);

const InputWrapper = styled.div`
  ${materialInputStyles}
  ${tw`relative border-slate-300! shadow-lg!`}
`;

function SignatureToggler({
  defaultTemplateSlug,
  injectSignature,
  editor,
  autofocus = true,
  contentRevision = 0
}) {
  const safeInjectSignature =
    injectSignature === 'true' ? true : injectSignature === 'false' ? false : injectSignature;

  const [signatureHtml, setSignatureHtml] = useState();

  const addSignature = () => {
    // This runs ~500ms after the signature is fetched, long after the form has
    // settled — which is why the jump felt delayed.
    //
    // Appending needs the selection at the end of the document, and that goes
    // through focus(). tiptap's focus() accepts `scrollIntoView`, so when the
    // caller opted out of autofocus we focus WITHOUT the browser scrolling the
    // editor into view. The insertion itself is untouched, so the signature
    // lands exactly where it always did.
    const focusOptions = autofocus ? {} : { scrollIntoView: false };

    editor.commands.focus('end', focusOptions);
    const { from } = editor.state.selection;
    const endPos = from;
    editor.commands.insertContent(`<p id="signatureSpacer"></p>${signatureHtml}`);
    editor.commands.focus(endPos, focusOptions);
  };

  const toggleSignature = ({ appendOnly = false }) => {
    let hasSignature = false;

    editor.commands.forEach(['signature'], (_, { tr, commands }) => {
      const item = findChildren(
        tr.doc,
        node => node.attrs.id === 'signature' || node.attrs.id === 'signatureSpacer'
      )?.[0];

      if (!item) {
        return true;
      }

      hasSignature = true;

      if (appendOnly) {
        return;
      }

      return commands.deleteRange({
        from: item.pos,
        to: item.pos + item.node.nodeSize
      });
    });

    if (!hasSignature) {
      addSignature();
    }
  };

  const hasSignature = () => {
    let found = false;

    if (editor) {
      editor.state.doc.descendants(node => {
        if (node.attrs.id === 'signature') {
          found = true;
        }
      });
    }

    return found;
  };

  const hasSig = hasSignature();

  useEffect(() => {
    function getSignature() {
      axios.get('/brand_members/signature.html').then(({ data }) => {
        const cleanSig = compressHTML(data);
        setSignatureHtml(cleanSig);
      });
    }

    getSignature();
  }, [editor]);

  // contentRevision is what makes this reliable. Appending used to depend on a
  // blind 500ms timer winning a race against fetchTemplates(), whose
  // setContent() replaces the whole document — and wipes the signature with it.
  // On a slow machine the templates land last and the signature disappears.
  // Bumping contentRevision after each setContent re-runs the append, and
  // appendOnly makes it a no-op when the signature is already there.
  useEffect(() => {
    if (safeInjectSignature && signatureHtml) {
      setTimeout(() => toggleSignature({ appendOnly: true }), 500);
    }
  }, [signatureHtml, defaultTemplateSlug, contentRevision]);

  return (
    <ToolbarBtn type="button" onClick={toggleSignature}>
      {hasSig ? (
        <svg
          className="w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      ) : (
        <svg
          className="w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
          />
        </svg>
      )}
      <span className="text-xs font-hmedium ml-1">
        {hasSig ? 'Retirer signature' : 'Ajouter signature'}
      </span>
    </ToolbarBtn>
  );
}

function makeid(length) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

function slugify(str) {
  return String(str)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function updateDraft({ identifier, body, files = [] }) {
  try {
    const currentDraftData = getDrafts();
    const newData = { ...currentDraftData, [identifier]: { body, files, timestamp: new Date() } };
    window.localStorage.setItem('draftMessages', JSON.stringify(newData));
  } catch (err) {
    console.error(err);
  }
}

function getDraft({ identifier }) {
  const currentDraftData = getDrafts();

  if (get(currentDraftData, identifier)) {
    return get(currentDraftData, identifier);
  } else {
    return { files: [], body: '' };
  }
}

function getDrafts() {
  const currentDrafts = window.localStorage.getItem('draftMessages');
  return currentDrafts ? JSON.parse(currentDrafts) : {};
}

function clearDrafts() {
  window.localStorage.removeItem('draftMessages');
}

function extractTextFromHTML(htmlString) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  const hasSignature = tempDiv.querySelector('#signature');
  if (hasSignature) {
    tempDiv.removeChild(tempDiv.querySelector('#signature'));
  }
  return tempDiv.textContent || tempDiv.innerText || '';
}

let draftTimeout = null;

function Message({
  placeholder = 'Rédiger un message',
  defaultTemplateSlug,
  bodyParamName = 'message[body]',
  filesParamName = 'message[files][]',
  allowTemplates = false,
  allowSubmit = false,
  allowFiles = false,
  allowSignature = false,
  allowExpand = false,
  padding = false,
  onChange,
  injectSignature = true,
  value = '',
  defaultFileIds = [],
  // Focusing the editor on mount makes the browser scroll it into view, which
  // throws the surrounding form down the page. Kept on by default so existing
  // callers are unaffected; a form that mounts this below the fold opts out.
  autofocus = true
}) {
  // Incremented whenever the document is replaced wholesale, so dependants can
  // react to content they did not cause.
  const [contentRevision, setContentRevision] = useState(0);
  const [storageIdentifier, setStorageIdentifier] = useState();
  const [currentDraft, setCurrentDrafts] = useState({});
  const [draftInUse, setDraftInUse] = useState(false);
  useEffect(() => {
    const id = makeid(20);
    if (id) {
      setStorageIdentifier(id);
    }
  }, []);

  const scrollableRef = useRef();
  const [templates, setTemplates] = useState([]);
  const [formValues, setFormValues] = useState({ files: [], body: value });
  const { shrinked, focused, handleBlur: onBlur, handleFocus: onFocus } = useInput('');

  const draftWithIdentifier = Object.entries(currentDraft).map(([key, value]) => ({
    ...value,
    identifier: key
  }));

  const filteredDrafts = draftWithIdentifier
    .reverse()
    .filter(
      d =>
        (extractTextFromHTML(d.body).length >= 2 || (d.files && d.files.length >= 1)) &&
        formValues.body !== d.body
    )
    .slice(0, 5);

  const hasDrafts = filteredDrafts.length >= 1;

  useEffect(() => setCurrentDrafts(getDrafts()), []);

  const { editor } = useRichText({
    value: value,
    placeholder,
    onBlur,
    onFocus: opts => {
      onFocus(opts);
      window.debouncedResizeDialogs();
    },
    onChange: body => {
      setFormValues({ ...formValues, body });

      if (onChange) {
        onChange(body);
      }
    }
  });

  useEffect(() => {
    if (storageIdentifier) {
      updateDraft({ ...formValues, identifier: storageIdentifier });
      setCurrentDrafts(getDrafts());
    }
  }, [formValues]);

  useEffect(() => {
    async function fetchFiles() {
      const { data } = await axios.get(`/blobs/lookup?blob_ids=${defaultFileIds.join(',')}`);
      setFormValues(prev => ({ ...prev, files: data }));
    }

    if (Array.isArray(defaultFileIds) && defaultFileIds.length >= 1) {
      fetchFiles();
    }
  }, []);

  const [expanded, setExpanded] = useState(false);

  const showSignature = ['true', 'false'] ? String(allowSignature) === 'true' : allowSignature;
  const showTemplates = ['true', 'false'] ? String(allowTemplates) === 'true' : allowTemplates;

  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.forEach(file => {
      const data = new FormData();

      data.append('file', file);

      axios.post('/attachments', data).then(response => {
        const { byteSize, filename, contentType, signedId } = response.data;

        setFormValues(prev => {
          const newFiles = [
            ...prev.files,
            {
              signedId,
              filename,
              contentType,
              byteSize: formatBytes(byteSize)
            }
          ];

          return {
            ...prev,
            files: newFiles
          };
        });
        scrollToBottom(scrollableRef);
      });
    });
  }, []);

  const files = get(formValues, 'files', []);
  const body = get(formValues, 'body', '') || '';
  const hasBody = cleanHtml(body).length >= 1;
  const hasContent = hasBody || files.length >= 1;

  const thumbs = files.map(file => (
    <div
      className="inline-flex items-center bg-slate-50 justify-between border border-slate-200 border-solid py-1 px-2 text-sm"
      key={file.signedId}
    >
      <div className="truncate font-hmedium">{file.filename}</div>
      <div className="flex items-center space-x-4">
        <div>{file.byteSize}</div>
        <div>{file.contentType}</div>
        <button
          type="button"
          onClick={() =>
            setFormValues(prev => {
              const newFiles = prev.files.filter(p => p.signedId !== file.signedId);

              return {
                ...prev,
                files: newFiles
              };
            })
          }
        >
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  ));

  const renderedFiles = files.length >= 1 && (
    <div className="max-w-[600px] flex flex-col space-y-2 ml-5 mb-6 mt-4">{thumbs}</div>
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const editorContent = (
    <StyledProseMirrorWrapper>
      {editor && <BubbleToolbar editor={editor} />}

      <div className="text-base">
        <EditorContent editor={editor} />
      </div>
    </StyledProseMirrorWrapper>
  );

  useEffect(() => {
    const node = scrollableRef.current.closest('[data-rich-text-wrapper-height]');

    if (node) {
      if (expanded) {
        node.style.height = node.dataset.richTextWrapperHeightMax || '75vh';
      } else {
        node.style.height = node.dataset.richTextWrapperHeight;
      }
    }
  }, [expanded]);

  function fetchTemplates() {
    axios.get('/template/messages.json').then(({ data }) => {
      const formattedTemplates = data
        .map(template => ({
          ...template,
          parent_id: 'SELF'
        }))
        .sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));

      const mappedTemplate =
        defaultTemplateSlug &&
        formattedTemplates.find(
          tpl =>
            get(tpl, 'slug') === defaultTemplateSlug ||
            get(tpl, 'parent_relation_ids').includes(defaultTemplateSlug) ||
            get(tpl, 'suggest_for_model.slug') === defaultTemplateSlug
        );

      if (mappedTemplate) {
        const desc = mappedTemplate.description;
        setFormValues({ ...formValues, body: desc });
        editor.commands.setContent(desc);
        setContentRevision(revision => revision + 1);

        if (onChange) {
          onChange(desc);
        }
      }

      setTemplates(formattedTemplates);
    });
  }

  useEffect(() => {
    if (editor) {
      if (showTemplates) {
        fetchTemplates();
      }

      if (autofocus) {
        editor.commands.focus('end');
      }
    }
  }, [editor, defaultTemplateSlug]);

  return (
    <InputWrapper
      data-target="inputWrapper"
      roundedFull
      shrinked={shrinked}
      focused={focused}
      padding={padding}
      fromTop
    >
      <div data-localstorage-draft-id={draftInUse} className={`absolute top-2 right-2 z-10`}>
        <Popover
          disablePadding
          padding
          maxHeight
          width
          buttonComponent={
            <div
              data-draft={storageIdentifier}
              className="w-5 h-5 flex items-center justify-center border font-hmedium !rounded-full"
            >
              <Heroicon.ClockIcon className="w-3 h-3" />
            </div>
          }
        >
          {({ setIsOpen }) => {
            return (
              <div className="text-[10px] w-[230px] leding-[12px] p-2">
                <div className="font-hmedium mb-2 text-xs px-2">Derniers messages non envoyés</div>
                {filteredDrafts.length <= 0 && (
                  <div className="px-2 py-4 border border-dashed border-solid flex items-center justify-center rounded-lg mx-2 mb-2">
                    Aucun message en attente
                  </div>
                )}
                {filteredDrafts.map(draft => {
                  return (
                    <button
                      onClick={e => {
                        e.preventDefault();
                        setDraftInUse(draft.identifier);

                        editor.commands.setContent(draft.body);
                        setFormValues(prev => ({
                          ...prev,
                          body: draft.body,
                          files: draft.files && draft.files.length >= 1 ? draft.files : prev.files
                        }));

                        if (onChange) {
                          onChange(draft.body);
                        }

                        setIsOpen(false);
                      }}
                      type="button"
                      className="truncate w-full flex flex-col leading-[12px] px-2 py-1 hover:bg-slate-100 rounded-lg cursor-pointer"
                    >
                      <span className="w-full truncate font-hmedium text-[11px]">
                        {extractTextFromHTML(draft.body)}
                      </span>
                      <span className="text-slate-500 text-[10px]">
                        <span className="mr-[2px]">
                          {moment(draft.timestamp).format('D/MM/YY à H:m')}
                        </span>
                        {draft.files && draft.files.length >= 1 && (
                          <span>
                            {draft.files.length >= 2
                              ? `• ${draft.files.length} pièces jointes`
                              : '• 1 pièce jointe'}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
                {filteredDrafts.length >= 1 && (
                  <button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      window.localStorage.removeItem('draftMessages');
                      setCurrentDrafts([]);
                    }}
                    className="mt-2 space-x-1 rounded-full text-[10px] bg-red-50 border border-red-100 hover:border-red-300 text-red-600 font-hmedium pl-1 pr-2 py-[1px] inline-flex items-center mx-2"
                  >
                    <Heroicon.XMarkIcon className="w-3 h-3" />
                    <span>Effacer</span>
                  </button>
                )}
              </div>
            );
          }}
        </Popover>
      </div>

      <div className="h-[calc(100%_-_50px)] overflow-hidden">
        <div className="mb-[50px] overflow-y-scroll max-h-full" ref={scrollableRef}>
          <div className="py-2 px-5">{editorContent}</div>
          {renderedFiles}
        </div>
      </div>
      <div className="h-[50px] border-t border-solid border-slate-200 rounded-b absolute bottom-0 w-full left-0 flex items-center justify-between px-5  overflow-x-scroll">
        <div className="flex items-center space-x-2">
          {allowSubmit && (
            <button
              className="truncate bg-blue-600 text-white rounded-full font-hmedium items-center flex py-1 px-3 text-sm disabled:opacity-50 disabled:pointer-events-none"
              disabled={!hasContent}
              type="submit"
              onClick={clearDrafts}
            >
              Envoyer le message
            </button>
          )}

          {editor && showTemplates && (
            <Templates
              buttonComponent={
                <ToolbarBtn type="button">
                  <CaretdownIcon classes="w-4 h-4" />
                  <span className="text-xs font-hmedium ml-1 truncate w-full">
                    Sélectionner un modèle
                  </span>
                </ToolbarBtn>
              }
              templates={templates}
              onSelectTemplate={htmlBody => {
                setFormValues({ ...formValues, body: htmlBody });
                if (onChange) {
                  onChange(htmlBody);
                }
                editor.commands.setContent(htmlBody);
              }}
            />
          )}

          {showSignature && (
            <SignatureToggler
              defaultTemplateSlug={defaultTemplateSlug}
              injectSignature={injectSignature}
              editor={editor}
              autofocus={autofocus}
              contentRevision={contentRevision}
            />
          )}

          {allowFiles && (
            <>
              <ToolbarBtn type="button" {...getRootProps()}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                  />
                </svg>
                <span className="text-xs font-hmedium ml-1">Fichiers</span>
              </ToolbarBtn>
              <input {...getInputProps()} />
            </>
          )}
        </div>
        <div className="flex items-center">
          {allowExpand && (
            <ToolbarBtn type="button" border={false} onClick={() => setExpanded(!expanded)}>
              {expanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                  />
                </svg>
              )}

              <span className="text-xs font-hmedium ml-1">{expanded ? 'Réduire' : 'Agrandir'}</span>
            </ToolbarBtn>
          )}
        </div>
      </div>

      {hasBody && <input type="hidden" name={bodyParamName} value={body || ''} />}

      {files.map(file => (
        <input
          type="hidden"
          name={filesParamName}
          value={file.signedId || file.signed_id}
          key={file.signedId || file.signed_id}
        />
      ))}
    </InputWrapper>
  );
}

export default Message;
